/**
 * Custom Tooltip Component
 *
 * Displays year-over-year comparison data when hovering over a bar.
 * Shows the current and previous year values with their respective date ranges.
 */

import React from 'react';
import { TooltipProps } from 'recharts';
import { HorizontalBarChartData } from './HorizontalBarChart';
import styles from './CustomTooltip.module.scss';

interface CustomTooltipProps extends TooltipProps<number, string> {
    showDots?: boolean;
    isCurrency?: boolean;
}

export const CustomTooltip: React.FC<CustomTooltipProps> = ({
    active,
    payload,
    showDots = false,
    isCurrency = false
}) => {
    if (!active || !payload?.length) return null;

    const data = payload[0].payload as HorizontalBarChartData;

    const formatValue = (value: number, share?: number): { text: string; percentage?: string } => {
        const formattedValue = isCurrency ? `$${value.toLocaleString()}` : value.toString();
        if (share !== undefined) {
            return { text: formattedValue, percentage: `(${share}%)` };
        }
        return { text: formattedValue };
    };

    const currentFormatted = formatValue(data.currentValue, data.currentShare);
    const previousFormatted = formatValue(data.previousValue, data.previousShare);

    return (
        <div className={styles.customTooltip}>
            <div className={styles.tooltipHeader}>{data.name}</div>
            <div className={styles.tooltipContent}>
                <div className={styles.tooltipItem}>
                    {showDots && <span className={styles.dotCurrent} />}
                    <span className={styles.tooltipLabel}>{data.currentDate}</span>
                    <span className={styles.tooltipValue}>
                        {currentFormatted.text}{' '}
                        {currentFormatted.percentage && (
                            <span className={styles.tooltipPercentage}>{currentFormatted.percentage}</span>
                        )}
                    </span>
                </div>
                <div className={styles.tooltipItem}>
                    {showDots && <span className={styles.dotPrevious} />}
                    <span className={styles.tooltipLabel}>{data.previousDate}</span>
                    <span className={styles.tooltipValue}>
                        {previousFormatted.text}{' '}
                        {previousFormatted.percentage && (
                            <span className={styles.tooltipPercentage}>{previousFormatted.percentage}</span>
                        )}
                    </span>
                </div>
            </div>
        </div>
    );
};
